import React, { useCallback, useEffect, useRef, useState, } from 'react';
import { Platform, StyleSheet, useWindowDimensions, View, FlatList, TouchableOpacity } from 'react-native';
import { ActivityIndicator, Card } from 'react-native-paper';
import { useTheme } from '@react-navigation/native';
import { createCommonStyles } from '../../../../styles/commonStyles';
import { SafeAreaView } from 'react-native-safe-area-context';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import CustomButton from '../../../../components/CustomButton';
import { useTranslation } from 'react-i18next';
import Animated from 'react-native-reanimated';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import CustomControlledDropdown from '../../../../components/ControlledComponents/CustomControlledDropdown';
import { showMessage } from 'react-native-flash-message';
import CustomInput from '../../../../components/CustomInput';

const ListView = React.memo(({
    index, styles, handleDelete, control, setValue, workflows, workflowObjs, useWatch, showDeleteIcon }) => {
    const id = useWatch({ control, name: `workflows.${index}.workflowID` });
    const [desc, setDesc] = useState(null);
    useEffect(() => {
        setDesc(workflowObjs?.[id]?.categoryDescription);
    }, [id])

    return (
        <SafeAreaView>
            <SafeAreaView style={styles.row}>
                <Animated.View
                    style={[styles.item, { justifyContent: 'flex-start' }]}>
                    <CustomControlledDropdown
                        multipleSelect={false}
                        control={control}
                        name={`workflows.${index}.workflowID`}
                        type='WORKFLOW'
                        options={workflows}
                        setValue={setValue}
                        disabled={false}
                    />
                </Animated.View>
                <Animated.View
                    style={[styles.item]}>
                    <CustomInput
                        value={desc}
                        editable={false}
                        type='MAININPUT'
                    />
                </Animated.View>
                {showDeleteIcon && (
                    <SafeAreaView style={[styles.margin, { flex: 1 }]}>
                        <CustomButton
                            iconName='trash-outline'
                            onPress={() => {
                                handleDelete(index)
                            }}
                            show='HOME_BELL'
                            type='HOME_BELL'
                            textTop='2'
                            sz={30} />
                    </SafeAreaView>
                )}
            </SafeAreaView>
        </SafeAreaView>
    )
})

export default function HRAdminWorkflows(props) {
    const { showDeleteIcon = true, showCancelButton = true, showDeleteButtonInHeader = false, onDeleteSelectedWorkflow,
        handleCancelChanges, onAddNewClick, defaultWorkflows = [], changeStep,
        employee, ref, workflows, workflowObjs, isLoading, setIsLoading,
        onSuccess, insertEmplWorkflow, handleEditPress, onSubmit: parentOnSubmit, handleSideMenuPress,
        pressedButtons, showSideMenu = false,
        SIDE_MENU_OPTIONS = [], buttonsContainerWidth = '100%',
    } = props;
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const { t } = useTranslation();
    const { control, handleSubmit, formState: { errors }, setValue, getValues, reset } = useForm({
        defaultValues: {
            workflows: [],
        },
    });
    const { fields, prepend, remove } = useFieldArray({
        control,
        name: 'workflows'
    });
    const selectedWorkflowIdFromForm = useWatch({ control, name: 'workflows.0.workflowID' });

    useEffect(() => {
        if (props.onWorkflowSelect && selectedWorkflowIdFromForm) {
            props.onWorkflowSelect(selectedWorkflowIdFromForm);
        }
    }, [selectedWorkflowIdFromForm]);

    //    useEffect(() => {
    //     if (!props.onWorkflowSelect) return;
    //     const val = selectedWorkflowIdFromForm;
    //     const placeholderValues = [null, undefined, '-- Select --'];
    //     const normalized = placeholderValues.includes(val) ? null : val;
    //     props.onWorkflowSelect(normalized);
    // }, [selectedWorkflowIdFromForm, props.onWorkflowSelect])


    useEffect(() => {
        if (defaultWorkflows.length > 0) {
            reset({ workflows: defaultWorkflows });
        } else {
            reset({ workflows: [{ workflowID: null, rowguid: uuidv4() }] });
        }
    }, [defaultWorkflows, workflowObjs, workflows, reset]);
    //#region UseEffects

    //#region Handlers
    const handleDelete = (index) => {
        remove(index);
    }

    const handleAddNew = () => {
        if (onAddNewClick) {
            onAddNewClick();
        } else {
            prepend({
                workflowID: null,
                rowguid: uuidv4(),
                employeeID: props?.employee.employeeID,
                company: props?.employee?.company
            })
        }
    }

    const onCancel = () => {
        reset({ workflows: defaultWorkflows });
        handleCancelChanges && handleCancelChanges();
        if (props.onWorkflowSelect) {
            const first = defaultWorkflows[0];
            const initialWorkflowId = first?.workflowID ?? null;
            props.onWorkflowSelect(initialWorkflowId);
        }
    }

    const onSubmit = async (data) => {
        if (parentOnSubmit) {
            try {
                await parentOnSubmit(data);
            } catch (error) {
                // Optionally handle error here
                console.error('Error in parent onSubmit:', error);
            }
            return;
        }
        // Fallback save logic if no parent onSubmit provided
        try {
            const res = await insertEmplWorkflow(data);
            if (res?.status === 200) {
                changeStep && changeStep();
                onSuccess && onSuccess();
                showMessage({
                    message: t('common:saveSuccessMessage'),
                    type: 'success',
                    duration: 3000,
                });
            } else {
                showMessage({
                    message: t('common:issueMessage'),
                    type: 'error',
                    duration: 3000,
                });
            }
        } catch (error) {
            console.error('Error saving workflow:', error);
            showMessage({
                message: t('common:issueMessage'),
                type: 'error',
                duration: 3000,
            });
        }
    };
    //#endregion

    //#region Renderers
    const renderData = useCallback(({ item, index }) => {
        return (
            <Card
                style={[common.cardstyle]} >
                <Card.Content>
                    <ListView
                        item={item}
                        index={index}
                        styles={styles}
                        handleDelete={handleDelete}
                        control={control}
                        setValue={setValue}
                        workflows={workflows}
                        workflowObjs={workflowObjs}
                        useWatch={useWatch}
                        showDeleteIcon={showDeleteIcon}
                    />
                </Card.Content>
            </Card>
        )
    }, [fields, workflows, workflowObjs, showDeleteIcon]);
    //#endregion

    //#region Loader
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            <SafeAreaView style={styles.rowView}>
                <SafeAreaView style={[styles.subMain]}>
                    <View style={styles.innerView}>
                        <View style={styles.branchesView}>
                            <View style={styles.container}>
                                <View style={styles.bottomBarContainer}>
                                    {/* Side menu buttons on the left */}
                                    {showSideMenu && (
                                        <>
                                            <View style={styles.sideMenuHorizontal}>
                                                {SIDE_MENU_OPTIONS.map((option, index) => (
                                                    <TouchableOpacity
                                                        key={option.key}
                                                        onPress={() => handleSideMenuPress(index)}
                                                        style={[
                                                            styles.subMenuButtonHorizontal,
                                                            pressedButtons[index] ? styles.selectedButton : null,
                                                            styles.flexButton,
                                                            index === SIDE_MENU_OPTIONS.length - 1 ? { marginRight: 15 } : { marginRight: 10 },
                                                        ]}
                                                    >
                                                        <CustomLabel
                                                            value={t(option.label)}
                                                            type="SUBTITLE"
                                                            style={{ color: colors.text }}
                                                        />
                                                    </TouchableOpacity>
                                                ))}
                                            </View>
                                            <View style={common.verticalDivider} />
                                        </>
                                    )}
                                    <View style={[styles.buttons, styles.functionalityButtons, {width: buttonsContainerWidth }]}>
                                        <CustomButton
                                            text={t('common:new')}
                                            onPress={() => { handleAddNew() }}
                                            type='BUTTON_ADD'
                                            textTop='0'
                                            sz={15}
                                        />
                                        {showDeleteButtonInHeader && (
                                            <CustomButton
                                                text={t('common:delete')}
                                                onPress={() => {
                                                    if (onDeleteSelectedWorkflow) {
                                                        onDeleteSelectedWorkflow();
                                                    } else {
                                                        showMessage({
                                                            message: t('common:noWorkflowSelected'),
                                                            type: 'warning',
                                                        });
                                                    }
                                                }}
                                                type="BUTTON_CANCEL"
                                                textTop='0'
                                                sz={15}
                                                style={{ marginLeft: 10 }}
                                            />
                                        )}
                                        {fields.length > 0 && !props.hideSaveButton && (
                                            <CustomButton
                                                text={t('common:save')}
                                                onPress={handleSubmit(onSubmit)}
                                                type='BUTTON_SAVE'
                                                isGradient={true}
                                                textTop='0'
                                                sz={15}

                                            />
                                        )}
                                        {showCancelButton && (
                                            <CustomButton
                                                text="Cancel"
                                                onPress={onCancel}
                                                type='BUTTON_CANCEL'
                                                textTop="0"
                                                sz={15}
                                                style={{ marginLeft: 10 }}
                                            />
                                        )}
                                    </View>
                                </View>
                                <SafeAreaView style={common.divider} />
                                <FlatList
                                    style={styles.fullWidth}
                                    data={fields}
                                    keyExtractor={(item) => item.rowguid}
                                    renderItem={renderData}
                                    scrollEnabled={true}
                                    refreshing={isLoading}
                                    ref={ref}
                                    ListHeaderComponent={
                                        fields.length > 0 &&
                                        <SafeAreaView>
                                            <View style={[styles.row, { marginRight: '3%' }]}>
                                                <CustomLabel
                                                    value={t('common:workflow')}
                                                    type='SUBTITLE' />
                                                <CustomLabel
                                                    value={t('common:workflowCategory')}
                                                    type='SUBTITLE' />
                                            </View>
                                        </SafeAreaView>
                                    }
                                    initialNumToRender={10}
                                    ListEmptyComponent={
                                        isLoading ?
                                            <ActivityIndicator animating={true} color={colors.blue} />
                                            :
                                            <CustomLabel
                                                value={t("common:noDataAvailable")}
                                                type="NO_BRANCHES"
                                            />
                                    }
                                />
                            </View>
                        </View>
                    </View>
                </SafeAreaView>
            </SafeAreaView >
        </SafeAreaView >
    );
}

const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
        marginHorizontal: 10,
    },
    subMain: {
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        width: '100%'
    },
    container: {
        flex: 1,
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    margin: {
        marginHorizontal: 10,
    },
    item: {
        flex: 9,
        justifyContent: 'center',
        margin: 5,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    icons: {
        fontSize: 25,
        margin: 10,
        color: colors.text_primary,
    },
    backButton: {
        alignSelf: 'flex-end'
    },
    innerView: {
        flex: 1,
    },
    branchesView: {
        flexDirection: 'row',
        height: '100%'
    },
    fullWidth: {
        height: '78%'
    },
    functionalityButtons: {
        justifyContent: 'flex-end',
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        // width: '50%',
        alignSelf: 'flex-end',
        marginRight: 15,
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },

    bottomBarContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 10,
        marginBottom: 10,
    },

    sideMenuHorizontal: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        justifyContent: 'space-evenly', 
        flex: 1,
    },

    subMenuButtonHorizontal: {
        flex: 1,
        paddingVertical: 6,
        marginHorizontal: 6,
        paddingHorizontal: 0,
        borderRadius: 10,
        backgroundColor: 'white',
        elevation: 2,
        shadowColor: 'rgba(92, 143, 26, 1)',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.06,
        shadowRadius: 6,
        justifyContent: 'center',
        alignItems: 'center',
    },

    selectedButton: {
        borderWidth: 2,
        borderColor: '#86BC25',
        backgroundColor: 'white',
        borderRadius: 10,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
    },
    flexButton: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
